import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { ORDER_TYPE, PAYMENT_METHOD, DEFAULT_TIMEZONE, type OrderType, type PaymentMethod, type RestaurantDeliveryConfig, formatPrice, checkDeliveryRadius } from '@chatealo/shared';
import { ArrowLeft, MapPin, Clock, MessageSquare, ChevronDown, Loader2, Hash, Truck, Signpost } from 'lucide-react';
import { useDeliveryLocation } from '../hooks/useDeliveryLocation';
import { hapticFeedback } from '../lib/telegram';

const DeliveryMapPicker = lazy(() => import('./DeliveryMapPicker').then(m => ({ default: m.DeliveryMapPicker })));

interface CheckoutFormData {
  orderType: OrderType;
  tableNumber?: string;
  notes?: string;
  paymentMethod: PaymentMethod;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
  deliveryAddress?: string;
  scheduledFor?: string;
}

interface TaxConfig {
  taxEnabled: boolean;
  taxRate: number;
  taxName: string;
}

interface CheckoutFormProps {
  onSubmit: (data: CheckoutFormData) => void;
  onBack: () => void;
  isLoading: boolean;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  chargesEnabled: boolean;
  schedulingEnabled?: boolean;
  deliveryConfig?: RestaurantDeliveryConfig | null;
  subtotal?: number; // Cart subtotal in cents, used for min order validation
  fees?: TaxConfig | null;
  restaurantLatitude?: number | null;
  restaurantLongitude?: number | null;
  /** Pre-filled table number from QR code deep link. When set, the field is read-only. */
  initialTableNumber?: string;
}

/**
 * Get the minimum schedulable datetime (1 hour from now) in Puerto Rico timezone
 * Returns { minDate, minTime } for the date and time inputs
 */
function getScheduleMinValues(): { minDate: string; maxDate: string; minDatetime: Date } {
  const now = new Date();
  const minDatetime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
  const maxDatetime = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now

  const formatDate = (d: Date) => {
    const year = d.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, year: 'numeric' });
    const month = d.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, month: '2-digit' });
    const day = d.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, day: '2-digit' });
    return `${year}-${month}-${day}`;
  };

  return {
    minDate: formatDate(minDatetime),
    maxDate: formatDate(maxDatetime),
    minDatetime,
  };
}

/**
 * Build an ISO datetime string with PR offset (-04:00) from date and time inputs
 */
function buildScheduledForISO(date: string, time: string): string {
  return `${date}T${time}:00-04:00`;
}

/** Reusable section wrapper */
function SectionCard({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <div
      className="animate-slide-up"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}
    >
      {children}
    </div>
  );
}

/** Price summary with subtotal, tax, delivery fee, and total */
function PriceSummary({ subtotal, fees, isDelivery, deliveryConfig }: {
  subtotal: number;
  fees?: TaxConfig | null;
  isDelivery: boolean;
  deliveryConfig?: RestaurantDeliveryConfig | null;
}) {
  if (subtotal <= 0) return null;

  const taxAmount = fees?.taxEnabled && fees.taxRate > 0
    ? Math.round(subtotal * (fees.taxRate / 100))
    : 0;
  const deliveryFee = isDelivery && deliveryConfig && deliveryConfig.fee > 0
    ? deliveryConfig.fee
    : 0;
  const total = subtotal + taxAmount + deliveryFee;

  return (
    <div>
      <label className="block text-sm font-medium text-tg-text mb-3">
        Resumen de pago
      </label>
      <div className="bg-tg-secondary-bg rounded-xl p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-tg-hint">Subtotal</span>
          <span className="text-tg-hint">{formatPrice(subtotal)}</span>
        </div>
        {taxAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-tg-hint">{fees?.taxName || 'Impuesto'} ({fees?.taxRate}%)</span>
            <span className="text-tg-hint">{formatPrice(taxAmount)}</span>
          </div>
        )}
        {deliveryFee > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-tg-hint">🚚 Delivery</span>
            <span className="text-tg-hint">{formatPrice(deliveryFee)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t border-tg-bg">
          <span className="font-semibold text-tg-text">Total</span>
          <span className="font-bold text-tg-text">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  );
}


/** Calculate total from subtotal, fees, and delivery config */
function calculateTotal(subtotal: number, fees?: TaxConfig | null, isDelivery?: boolean, deliveryConfig?: RestaurantDeliveryConfig | null): number {
  const taxAmount = fees?.taxEnabled && fees.taxRate > 0
    ? Math.round(subtotal * (fees.taxRate / 100))
    : 0;
  const deliveryFee = isDelivery && deliveryConfig && deliveryConfig.fee > 0
    ? deliveryConfig.fee
    : 0;
  return subtotal + taxAmount + deliveryFee;
}

export function CheckoutForm({ onSubmit, onBack, isLoading, pickupEnabled, deliveryEnabled, chargesEnabled, schedulingEnabled = true, deliveryConfig, subtotal = 0, fees, restaurantLatitude, restaurantLongitude, initialTableNumber }: CheckoutFormProps) {
  // Default to first available option — if table is pre-set via QR, force PICKUP
  const defaultOrderType = initialTableNumber ? ORDER_TYPE.PICKUP : (pickupEnabled ? ORDER_TYPE.PICKUP : ORDER_TYPE.DELIVERY);
  const [orderType, setOrderType] = useState<OrderType>(defaultOrderType);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PAYMENT_METHOD.CASH);
  const [tableNumber, setTableNumber] = useState(initialTableNumber ?? '');
  const isTableLocked = Boolean(initialTableNumber);
  const [notes, setNotes] = useState('');
  // Delivery location
  const { deliveryLocation, deliveryAddress, locationLoading, locationError, handleRequestLocation, setDeliveryAddress, setDeliveryLocation } = useDeliveryLocation();
  // Scheduled order state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const scheduleMinValues = useMemo(() => getScheduleMinValues(), []);

  // Update orderType if the selected option becomes unavailable
  useEffect(() => {
    if (orderType === ORDER_TYPE.PICKUP && !pickupEnabled) {
      setOrderType(ORDER_TYPE.DELIVERY);
    } else if (orderType === ORDER_TYPE.DELIVERY && !deliveryEnabled) {
      setOrderType(ORDER_TYPE.PICKUP);
    }
  }, [pickupEnabled, deliveryEnabled, orderType]);

  const isDelivery = orderType === ORDER_TYPE.DELIVERY;
  const isScheduleValid = !isScheduled || (scheduledDate !== '' && scheduledTime !== '');
  const meetsMinOrder = !isDelivery || !deliveryConfig?.minOrder || subtotal >= deliveryConfig.minOrder;

  // Delivery radius validation
  const radiusCheck = useMemo(() => {
    if (
      !isDelivery ||
      !deliveryLocation ||
      !deliveryConfig?.maxRadiusKm ||
      restaurantLatitude == null ||
      restaurantLongitude == null
    ) {
      return null;
    }
    return checkDeliveryRadius(
      restaurantLatitude,
      restaurantLongitude,
      deliveryLocation.latitude,
      deliveryLocation.longitude,
      deliveryConfig.maxRadiusKm,
    );
  }, [isDelivery, deliveryLocation, deliveryConfig?.maxRadiusKm, restaurantLatitude, restaurantLongitude]);

  const isWithinRadius = radiusCheck === null || radiusCheck.isWithinRadius;
  const canSubmit = !isLoading && (pickupEnabled || deliveryEnabled) && (!isDelivery || deliveryLocation !== null) && isScheduleValid && meetsMinOrder && isWithinRadius;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    hapticFeedback('impact', 'medium');
    onSubmit({
      orderType,
      tableNumber: tableNumber.trim() || undefined,
      notes: notes.trim() || undefined,
      paymentMethod,
      deliveryLatitude: deliveryLocation?.latitude,
      deliveryLongitude: deliveryLocation?.longitude,
      deliveryAddress: deliveryAddress.trim() || undefined,
      scheduledFor: isScheduled && scheduledDate && scheduledTime
        ? buildScheduledForISO(scheduledDate, scheduledTime)
        : undefined,
    });
  };

  const handleToggleSchedule = () => {
    hapticFeedback('selection');
    const newValue = !isScheduled;
    setIsScheduled(newValue);
    if (!newValue) {
      setScheduledDate('');
      setScheduledTime('');
    }
  };

  const handlePaymentMethodChange = (method: PaymentMethod) => {
    hapticFeedback('selection');
    setPaymentMethod(method);
  };

  const handleOrderTypeChange = (type: OrderType, enabled: boolean) => {
    if (enabled) {
      hapticFeedback('selection');
      setOrderType(type);
    }
  };

  const handleBack = () => {
    hapticFeedback('impact', 'light');
    onBack();
  };

  const [showNotes, setShowNotes] = useState(notes.length > 0);

  const handleToggleNotes = () => {
    hapticFeedback('selection');
    setShowNotes((v) => !v);
  };

  return (
    <div className="fixed inset-0 bg-tg-bg z-50 flex flex-col safe-area-insets">
      <header className="flex items-center justify-between px-4 py-3 border-b border-tg-secondary-bg">
        <button
          onClick={handleBack}
          className="p-2 rounded-full bg-tg-secondary-bg text-tg-link active:scale-95 transition-transform"
        >
          <ArrowLeft size={24} />
        </button>
        <h2 className="text-lg font-bold text-tg-text">Confirmar pedido</h2>
        <div className="w-10" />
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-4">

          {/* ── 1. Order Type ── */}
          <SectionCard delay={0}>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                disabled={!pickupEnabled}
                onClick={() => handleOrderTypeChange(ORDER_TYPE.PICKUP, pickupEnabled)}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all active:scale-[0.97] ${
                  !pickupEnabled
                    ? 'opacity-40 cursor-not-allowed text-tg-hint'
                    : orderType === ORDER_TYPE.PICKUP
                      ? 'bg-tg-button text-tg-button-text shadow-md'
                      : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                <span className="text-lg">🏃</span>
                <span className="text-sm font-semibold">Pickup</span>
              </button>
              <button
                type="button"
                disabled={!deliveryEnabled}
                onClick={() => handleOrderTypeChange(ORDER_TYPE.DELIVERY, deliveryEnabled)}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all active:scale-[0.97] ${
                  !deliveryEnabled
                    ? 'opacity-40 cursor-not-allowed text-tg-hint'
                    : orderType === ORDER_TYPE.DELIVERY
                      ? 'bg-tg-button text-tg-button-text shadow-md'
                      : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                <span className="text-lg">🚗</span>
                <span className="text-sm font-semibold">Delivery</span>
              </button>
            </div>
          </SectionCard>

          {/* ── 2. Detalles del pedido ── */}
          <SectionCard delay={50}>
            <label className="block text-sm font-medium text-tg-text mb-3">
              Detalles del pedido
            </label>
            <div className="space-y-3">
              {/* Table number (pickup only) */}
              {orderType === ORDER_TYPE.PICKUP && (
                <div className="flex items-center gap-3 py-3 rounded-xl px-4 bg-tg-secondary-bg">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                    <Hash size={20} className={tableNumber.trim() ? 'text-tg-link' : 'text-tg-hint'} />
                  </div>
                  {isTableLocked ? (
                    <span className="flex-1 text-tg-text font-medium">Mesa {tableNumber}</span>
                  ) : (
                    <>
                      <select
                        id="tableNumber"
                        value={tableNumber}
                        onChange={(e) => setTableNumber(e.target.value)}
                        className="flex-1 bg-transparent text-tg-text border-none outline-none appearance-none"
                        style={{ WebkitAppearance: 'none' }}
                      >
                        <option value="" className="bg-tg-secondary-bg text-tg-text">Número de mesa (opcional)</option>
                        {Array.from({ length: 20 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1)} className="bg-tg-secondary-bg text-tg-text">
                            Mesa {i + 1}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="text-tg-hint shrink-0" />
                    </>
                  )}
                </div>
              )}

              {/* Delivery location */}
              {isDelivery && (
                <div className="space-y-3">
                  {!deliveryLocation && (
                    <button
                      type="button"
                      onClick={handleRequestLocation}
                      disabled={locationLoading}
                      className="w-full flex items-center gap-3 py-3 bg-tg-secondary-bg rounded-xl px-4 transition-all active:scale-[0.98] disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                        {locationLoading ? (
                          <Loader2 size={20} className="text-tg-hint animate-spin" />
                        ) : (
                          <MapPin size={20} className="text-tg-hint" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <span className="font-medium text-tg-text block">
                          {locationLoading ? 'Obteniendo ubicación...' : 'Compartir ubicación'}
                        </span>
                        <span className="text-xs text-tg-hint">
                          Requerido para delivery
                        </span>
                      </div>
                    </button>
                  )}
                  {locationError && (
                    <p className="text-xs text-red-500 px-4">{locationError}</p>
                  )}
                  {deliveryLocation && (
                    <>
                      {/* Interactive map with draggable pin and GPS button */}
                      <Suspense fallback={<div className="h-[200px] bg-tg-secondary-bg rounded-xl animate-pulse" />}>
                        <DeliveryMapPicker
                          latitude={deliveryLocation.latitude}
                          longitude={deliveryLocation.longitude}
                          onLocationChange={(lat, lng) => {
                            setDeliveryLocation({ latitude: lat, longitude: lng });
                          }}
                          restaurantLatitude={restaurantLatitude}
                          restaurantLongitude={restaurantLongitude}
                          height={200}
                          onRequestLocation={handleRequestLocation}
                          locationLoading={locationLoading}
                        />
                      </Suspense>
                      <div className="flex items-center gap-3 py-3 bg-tg-secondary-bg rounded-xl px-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                          <Signpost size={20} className="text-tg-hint" />
                        </div>
                        <input
                          type="text"
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          maxLength={500}
                          placeholder="Dirección o referencia (opcional)"
                          className="flex-1 bg-transparent text-tg-text placeholder-tg-hint border-none outline-none"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Delivery fee info */}
              {isDelivery && deliveryConfig && deliveryConfig.fee > 0 && (
                <div className="flex items-center gap-3 py-3 bg-tg-secondary-bg rounded-xl px-4">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                    <Truck size={20} className="text-tg-hint" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-tg-text block text-sm">Cargo de delivery</span>
                    {deliveryConfig.estimatedTime && (
                      <span className="text-xs text-tg-hint">{deliveryConfig.estimatedTime}</span>
                    )}
                  </div>
                  <span className="font-semibold text-tg-text">{formatPrice(deliveryConfig.fee)}</span>
                </div>
              )}

              {/* Notes (collapsible) */}
              <div>
                <button
                  type="button"
                  onClick={handleToggleNotes}
                  className="w-full flex items-center gap-3 py-3 bg-tg-secondary-bg rounded-xl px-4"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                    <MessageSquare size={20} className={showNotes ? 'text-tg-link' : 'text-tg-hint'} />
                  </div>
                  <div className="flex-1 text-left">
                    <span className="font-medium text-tg-text block">
                      {notes.length > 0 ? 'Notas añadidas' : 'Agregar notas'}
                    </span>
                    <span className="text-xs text-tg-hint">Comentarios al negocio</span>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-tg-hint transition-transform ${showNotes ? 'rotate-180' : ''}`}
                  />
                </button>
                {showNotes && (
                  <div className="mt-3">
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      maxLength={500}
                      placeholder="Instrucciones especiales, alergias, etc."
                      rows={3}
                      className="w-full px-4 py-3 bg-tg-secondary-bg rounded-xl text-tg-text placeholder-tg-hint border-none outline-none resize-none"
                    />
                    <div className="text-xs text-tg-hint text-right mt-1">
                      {notes.length}/500
                    </div>
                  </div>
                )}
              </div>

              {/* Schedule */}
              {schedulingEnabled && (
                <div>
                  <button
                    type="button"
                    onClick={handleToggleSchedule}
                    className="w-full flex items-center gap-3 py-3 bg-tg-secondary-bg rounded-xl px-4"
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-tg-bg">
                      <Clock size={20} className={isScheduled ? 'text-tg-link' : 'text-tg-hint'} />
                    </div>
                    <div className="flex-1 text-left">
                      <span className="font-medium text-tg-text block">
                        Programar para después
                      </span>
                      <span className="text-xs text-tg-hint">Elige fecha y hora</span>
                    </div>
                    <div className={`w-14 h-8 rounded-full transition-colors relative ${
                      isScheduled ? 'bg-blue-500' : 'bg-gray-400'
                    }`}>
                      <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-all ${
                        isScheduled ? 'right-1' : 'left-1'
                      }`} />
                    </div>
                  </button>

                  {isScheduled && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="scheduledDate" className="block text-sm font-medium text-tg-text mb-2">Fecha</label>
                        <input
                          type="date"
                          id="scheduledDate"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={scheduleMinValues.minDate}
                          max={scheduleMinValues.maxDate}
                          className="w-full px-4 py-3 bg-tg-secondary-bg rounded-xl text-tg-text appearance-none"
                          style={{ WebkitAppearance: 'none', minHeight: '48px' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="scheduledTime" className="block text-sm font-medium text-tg-text mb-2">Hora</label>
                        <input
                          type="time"
                          id="scheduledTime"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          className="w-full px-4 py-3 bg-tg-secondary-bg rounded-xl text-tg-text appearance-none"
                          style={{ WebkitAppearance: 'none', minHeight: '48px' }}
                        />
                      </div>
                      <p className="col-span-2 text-xs text-tg-hint">
                        Mínimo 1 hora desde ahora · Máximo 30 días
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── 5. Payment Method ── */}
          <SectionCard delay={200}>
            <label className="block text-sm font-medium text-tg-text mb-2">
              Método de pago
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handlePaymentMethodChange(PAYMENT_METHOD.CASH)}
                className={`flex flex-col items-center justify-center gap-1 py-5 rounded-2xl font-medium transition-all active:scale-[0.97] ${
                  paymentMethod === PAYMENT_METHOD.CASH
                    ? 'bg-tg-button text-tg-button-text shadow-md'
                    : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                <span className="text-3xl">💵</span>
                <span className="text-sm font-semibold">Efectivo</span>
              </button>
              <button
                type="button"
                disabled={!chargesEnabled}
                onClick={() => chargesEnabled && handlePaymentMethodChange(PAYMENT_METHOD.CARD)}
                className={`flex flex-col items-center justify-center gap-1 py-5 rounded-2xl font-medium transition-all active:scale-[0.97] ${
                  !chargesEnabled
                    ? 'opacity-40 cursor-not-allowed bg-tg-secondary-bg text-tg-hint'
                    : paymentMethod === PAYMENT_METHOD.CARD
                      ? 'bg-tg-button text-tg-button-text shadow-md'
                      : 'bg-tg-secondary-bg text-tg-text'
                }`}
              >
                <span className="text-3xl">💳</span>
                <span className="text-sm font-semibold">Tarjeta</span>
              </button>
            </div>
            {paymentMethod === PAYMENT_METHOD.CARD && (
              <p className="text-xs text-tg-hint mt-2 text-center">
                Serás redirigido a una página segura de pago
              </p>
            )}
          </SectionCard>

          {/* ── 6. Price Summary (inside scroll) ── */}
          {subtotal > 0 && (
            <SectionCard delay={250}>
              <PriceSummary subtotal={subtotal} fees={fees} isDelivery={isDelivery} deliveryConfig={deliveryConfig} />
            </SectionCard>
          )}

        </div>
      </div>

      {/* ── Submit ── */}
      <form onSubmit={handleSubmit} className="p-4 bg-gradient-to-t from-tg-bg via-tg-bg to-transparent pb-6">
        {!pickupEnabled && !deliveryEnabled && (
          <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <p className="text-sm text-amber-600 dark:text-amber-400 text-center">
              ⚠️ El local no acepta pedidos en este momento
            </p>
          </div>
        )}
        {isDelivery && !meetsMinOrder && deliveryConfig?.minOrder && (
          <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <p className="text-sm text-amber-600 dark:text-amber-400 text-center">
              Pedido mínimo de {formatPrice(deliveryConfig.minOrder)} para delivery
            </p>
          </div>
        )}
        {isDelivery && radiusCheck && !radiusCheck.isWithinRadius && deliveryConfig?.maxRadiusKm && (
          <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-sm text-red-600 dark:text-red-400 text-center">
              📍 Tu ubicación está a {radiusCheck.distanceKm} km. El radio máximo de entrega es {deliveryConfig.maxRadiusKm} km.
            </p>
          </div>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-3 px-5 bg-tg-button text-tg-button-text rounded-full font-semibold disabled:opacity-50 shadow-lg active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              {isScheduled ? 'Programar pedido' : 'Enviar pedido'}
              {subtotal > 0 && (
                <span className="opacity-80">· {formatPrice(calculateTotal(subtotal, fees, isDelivery, deliveryConfig))}</span>
              )}
            </>
          )}
        </button>
      </form>
    </div>
  );
}

